import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Mine {
  public mesh: THREE.Group;
  public body: CANNON.Body;
  public isActive: boolean = true;
  public owner: 'player' | 'enemy';
  public damage: number;
  public areaOfEffect: number;
  public hasExploded: boolean = false;
  public isArmed: boolean = false; // Mines need arming time to prevent self-damage

  private armingTime: number = 1.0; // 1 second to arm
  private age: number = 0;
  private triggerRadius: number = 2; // Detection radius
  private blinkTimer: number = 0;
  private blinkInterval: number = 0.5;
  private indicatorLight: THREE.Mesh;

  constructor(
    position: THREE.Vector3,
    owner: 'player' | 'enemy',
    damage: number,
    areaOfEffect: number
  ) {
    this.owner = owner;
    this.damage = damage;
    this.areaOfEffect = areaOfEffect;

    // 创建地雷网格组
    this.mesh = new THREE.Group();

    // 地雷主体（圆盘形）
    const bodyGeometry = new THREE.CylinderGeometry(0.6, 0.5, 0.3, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.8,
      roughness: 0.2
    });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
    this.mesh.add(bodyMesh);

    // 顶部指示灯
    const lightGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.1, 8);
    const lightMaterial = new THREE.MeshStandardMaterial({
      color: owner === 'player' ? 0x00ff00 : 0xff0000,
      emissive: owner === 'player' ? 0x00ff00 : 0xff0000,
      emissiveIntensity: 0
    });
    this.indicatorLight = new THREE.Mesh(lightGeometry, lightMaterial);
    this.indicatorLight.position.y = 0.2;
    this.mesh.add(this.indicatorLight);

    // 触发器（隐形）
    const triggerGeometry = new THREE.CylinderGeometry(this.triggerRadius, this.triggerRadius, 0.5, 16);
    const triggerMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0
    });
    const triggerMesh = new THREE.Mesh(triggerGeometry, triggerMaterial);
    triggerMesh.position.y = 0.25;
    this.mesh.add(triggerMesh);

    this.mesh.position.copy(position);

    // 创建物理体（静态）
    const shape = new CANNON.Cylinder(0.6, 0.5, 0.3, 8);
    this.body = new CANNON.Body({
      mass: 0, // Static
      shape: shape,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      collisionResponse: false // Doesn't physically collide
    });

    // 添加触发器传感器
    const triggerShape = new CANNON.Cylinder(this.triggerRadius, this.triggerRadius, 0.5, 16);
    this.body.addShape(triggerShape, new CANNON.Vec3(0, 0.25, 0));

    // 添加用户数据
    this.body.userData = {
      type: 'mine',
      mine: this,
      owner: owner
    };
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    this.age += deltaTime;

    // 武装地雷
    if (!this.isArmed && this.age >= this.armingTime) {
      this.isArmed = true;
      console.log('Mine armed!');
    }

    // 指示灯闪烁
    if (this.isArmed) {
      this.blinkTimer += deltaTime;
      if (this.blinkTimer >= this.blinkInterval) {
        this.blinkTimer = 0;
        const material = this.indicatorLight.material as THREE.MeshStandardMaterial;
        material.emissiveIntensity = material.emissiveIntensity > 0 ? 0 : 1;
      }
    }
  }

  // Check if an entity is within trigger radius
  checkTrigger(position: THREE.Vector3, entityType: string): boolean {
    if (!this.isArmed || this.hasExploded) return false;

    // Don't trigger on owner's type
    if (this.owner === 'player' && entityType === 'tank') return false;
    if (this.owner === 'enemy' && entityType.includes('enemy')) return false;

    const distance = this.mesh.position.distanceTo(position);
    return distance <= this.triggerRadius;
  }

  explode(): void {
    if (this.hasExploded) return;
    this.hasExploded = true;
    this.destroy();
  }

  destroy(): void {
    this.isActive = false;
  }

  getExplosionPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }
}
